import React, { useMemo } from 'react';
import { useSelector } from "react-redux";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer, LabelList } from 'recharts';
import typeOfContract from '../../../../constants/typeOfContract';
import { clientTotalRevenueHistory } from './Services/reportRevenue';

const RevenueHistory = ({ clientInvoices, clientInvoicesLoading, clientInvoicesError, trackReport, trackReportLoading, trackReportError, projectCost, expenses }) => {
  const reportingRevenue = useSelector((state) => state.reportingRevenue);
  const { forex } = useSelector((state) => state.forex);
  const settings = JSON.parse(localStorage.getItem("settings"));

  const setReportingCurrency = useSelector((state) => state.reportingRevenue.revenueCurrency);
  var currencies = "USD";
  if (setReportingCurrency && setReportingCurrency.length === 1) {
    currencies = setReportingCurrency[0];
  } else {
    currencies = settings?.currency || "USD";
  }

  const processedData = useMemo(() => {
    if (!trackReport || !projectCost || !clientInvoices || !expenses || !reportingRevenue || !forex) {
      return [];
    }
    try {
      return clientTotalRevenueHistory(
        trackReport,
        projectCost,
        clientInvoices,
        reportingRevenue,
        currencies,
        forex,
        typeOfContract,
        expenses
      ) || [];
    } catch (error) {
      console.error('Error processing revenue history data:', error);
      return [];
    }
  }, [trackReport, projectCost, clientInvoices, reportingRevenue, currencies, forex, typeOfContract, expenses]);

  const calculateYTicks = () => {
    if (!processedData || processedData.length === 0) return [0];
    const dataValues = processedData.map(item => item.revenue || 0);
    const maxDataValue = Math.max(...dataValues);
    const minDataValue = Math.min(...dataValues);

    const hasNegativeValues = minDataValue < 0;
    const hasPositiveValues = maxDataValue > 0;

    // Determine tick interval
    const tickInterval = Math.ceil(Math.max(Math.abs(maxDataValue), Math.abs(minDataValue)) / 10);
    const numOfTicks = Math.min(Math.ceil(Math.max(Math.abs(maxDataValue), Math.abs(minDataValue)) / tickInterval), 10);
    const ticks = [];

    // Generate ticks for both positive and negative ranges
    if (hasNegativeValues && hasPositiveValues) {
      for (let i = -numOfTicks; i <= numOfTicks; i++) {
        ticks.push(i * tickInterval);
      }
    } else if (hasNegativeValues) {
      // Generate negative ticks only
      for (let i = -numOfTicks; i <= 0; i++) {
        ticks.push(i * tickInterval);
      }
    } else if (hasPositiveValues) {
      // Generate positive ticks only
      for (let i = 0; i <= numOfTicks; i++) {
        ticks.push(i * tickInterval);
      }
    }

    return ticks;
  }

  const yAxisTicks = calculateYTicks();

  if (clientInvoicesLoading || trackReportLoading) {
    const skeletonRows = 12; // 12 months
    const rowHeight = 40;
    const baseHeight = 120;
    const skeletonHeight = skeletonRows * rowHeight + baseHeight;
    
    // Fixed widths for skeleton bars to avoid random values
    const barWidths = [45, 60, 35, 55, 40, 50, 38, 48, 42, 52, 36, 46];
    
    return (
      <div className='graph-project-per-hour' style={{ width: '100%', height: Math.max(542, skeletonHeight) }}>
        <div style={{ 
          width: '100%', 
          height: '100%', 
          padding: '20px 42px 8px 0px',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Y-axis skeleton labels (currency values) - vertical on left */}
          <div style={{ 
            position: 'absolute', 
            left: '0', 
            top: '20px', 
            bottom: '8px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            paddingRight: '10px',
            width: '120px'
          }}>
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                style={{
                  width: '80px',
                  height: '12px',
                  backgroundColor: '#e5e7eb',
                  borderRadius: '4px',
                  animation: 'pulse 1.5s ease-in-out infinite',
                }}
              />
            ))}
          </div>

          {/* Chart area with bars */}
          <div style={{ 
            marginLeft: '130px',
            marginTop: '20px',
            marginBottom: '60px',
            height: 'calc(100% - 80px)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            position: 'relative'
          }}>
            {/* Horizontal grid lines skeleton */}
            <div style={{ 
              position: 'absolute',
              top: '0',
              left: '0',
              right: '42px',
              bottom: '0',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between'
            }}>
              {Array.from({ length: 6 }).map((_, index) => (
                <div
                  key={index}
                  style={{
                    width: '100%',
                    height: '1px',
                    background: 'repeating-linear-gradient(to right, #D6D9DB 0px, #D6D9DB 3px, transparent 3px, transparent 6px)',
                    opacity: 0.3
                  }}
                />
              ))}
            </div>

            {/* Horizontal bars skeleton */}
            {Array.from({ length: skeletonRows }).map((_, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  height: `${rowHeight}px`,
                  position: 'relative'
                }}
              >
                {/* Single horizontal bar skeleton */}
                <div
                  style={{
                    width: `${barWidths[index] || 45}%`,
                    height: '13px',
                    backgroundColor: '#96A8BA',
                    opacity: 0.3,
                    borderRadius: '2px',
                    animation: 'pulse 1.5s ease-in-out infinite',
                  }}
                />
              </div>
            ))}
          </div>

          {/* X-axis skeleton ticks (month names) - horizontal at bottom */}
          <div style={{ 
            position: 'absolute', 
            bottom: '8px', 
            left: '130px', 
            right: '42px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            height: '60px'
          }}>
            {Array.from({ length: 12 }).map((_, index) => (
              <div
                key={index}
                style={{
                  width: '40px',
                  height: '12px',
                  backgroundColor: '#e5e7eb',
                  borderRadius: '4px',
                  animation: 'pulse 1.5s ease-in-out infinite',
                  transform: 'rotate(-55deg)',
                  transformOrigin: 'bottom left',
                  marginTop: '20px'
                }}
              />
            ))}
          </div>
        </div>
        <style>{`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
        `}</style>
      </div>
    );
  }

  if (clientInvoicesError || trackReportError) {
    return <div>Error loading data.</div>;
  }

  if (!processedData || processedData.length === 0) {
    return <div style={{ width: '100%', height: 542, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>No data to display</div>;
  }

  return (
    <div className='graph-project-per-hour' style={{ width: '100%', height: 542 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          layout="horizontal"
          data={processedData}
          barSize={13}
          margin={{
            top: 20,
            right: 42,
            left: Math.max(
              0,
              new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: currencies || 'USD',
              })
                .format(Math.max(...processedData.map((item) => item.revenue || 0)))
                .length
            ),
            bottom: 8,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
          <XAxis
            type="category"
            dataKey="name"
            angle={-55}
            tickMargin={10}
          />
          <YAxis
            type="number"
            domain={['auto', 'auto']}
            tickFormatter={(tick) => `${new Intl.NumberFormat("en-US", { style: "currency", currency: currencies || 'USD' }).format(tick)}`}
            ticks={[
              ...(yAxisTicks.includes(Math.min(...yAxisTicks))
                ? [Math.min(...yAxisTicks) * 1.2]
                : []),
              ...yAxisTicks,
            ]}
          />
          <Tooltip formatter={(value) => `${new Intl.NumberFormat("en-US", { style: "currency", currency: currencies || 'USD' }).format(value)}`} />
          <ReferenceLine y={0} stroke="#000" />
          <Bar dataKey="revenue" name="Closed amount in default currency" fill="#96A8BA" barSize={13}>
            <LabelList dataKey="revenue" position="top" fill="#282828" formatter={(value) => `${new Intl.NumberFormat("en-US", { style: "currency", currency: currencies || 'USD' }).format(value)}`} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default RevenueHistory;
